import React, { useEffect, useMemo, useRef, useState } from "react";

const normalizeWord = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.,!?;:()[\]{}"“”'`]+$/g, "");
};

const getWordScoreColor = (score, errorType) => {
  if (errorType === "Omission") return "bg-gray-400";
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getPhonemeDotColor = (score) => {
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getWordAssessmentClasses = (score, errorType) => {
  if (errorType === "Omission") {
    return "bg-gray-50 border-gray-300 text-gray-800";
  }

  if (typeof score !== "number") {
    return "bg-white/80 border-white/40 text-gray-800";
  }

  if (score >= 80) {
    return "bg-green-50 border-green-200 text-gray-800";
  }

  if (score >= 40) {
    return "bg-yellow-50 border-yellow-200 text-gray-800";
  }

  return "bg-red-50 border-red-200 text-gray-800";
};

const getActiveRingClass = (practiceMode) => {
  if (practiceMode === "shadowing") {
    return "ring-2 ring-orange-500 border-orange-300 shadow-lg shadow-orange-500/20 scale-105";
  }

  if (practiceMode === "echoing") {
    return "ring-2 ring-blue-500 border-blue-300 shadow-lg shadow-blue-500/20 scale-105";
  }

  return "ring-2 ring-emerald-500 border-emerald-300 shadow-lg shadow-emerald-500/20 scale-105";
};

const buildSegmentAssessmentMap = (segmentWords = [], assessmentWords = []) => {
  let pointer = 0;

  return segmentWords.map((item) => {
    const segmentText = normalizeWord(item?.text);

    if (!segmentText) return null;

    for (let i = pointer; i < assessmentWords.length; i++) {
      if (normalizeWord(assessmentWords[i]?.Word) === segmentText) {
        pointer = i + 1;
        return assessmentWords[i];
      }
    }

    const fallback =
      assessmentWords.find(
        (word) => normalizeWord(word?.Word) === segmentText
      ) || null;

    return fallback;
  });
};

const SentencePracticeBlock = ({
  segment,
  segments,
  assessment,
  renderHelpPill,
  renderCommentsPill,
  isMetadataNoHelp,
  setShowExerciseHelp,
  handlePreviousSegment,
  handleNextSegment,
  showCenterCue,
  practiceMode,
  echoingPhase,
  isCueActive,
  recordingStatus,
  cuePulseCount,
  echoingPulseCount,
  sentenceLabel,
  currentWord,
  handleWordPlay,
  openWordMenu,
  handleRangePlay,
  showWords = true,
  enableWordActions = true,
  isPlayingSegment = false,
  showWordLane = true,
  
}) => {
  const selectionAreaRef = useRef(null);

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);

  const normalizedSelection = useMemo(() => {
    if (selectionStart == null || selectionEnd == null) return null;

    return {
      start: Math.min(selectionStart, selectionEnd),
      end: Math.max(selectionStart, selectionEnd),
    };
  }, [selectionStart, selectionEnd]);

  const clearSelection = () => {
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setShowSelectionMenu(false);
  };

  const playSelectedRange = () => {
    if (!normalizedSelection || !segment?.words?.length) return;

    const { start, end } = normalizedSelection;

    handleRangePlay?.({
      startIndex: start,
      endIndex: end,
    });
  };

  const handleWordMouseDown = (index, e) => {
    if (!enableWordActions || practiceMode === "shadowing") return;

    e.preventDefault();
    setIsSelecting(true);
    setSelectionStart(index);
    setSelectionEnd(index);
    setShowSelectionMenu(false);
  };

  const handleWordMouseEnter = (index) => {
    if (!isSelecting) return;
    setSelectionEnd(index);
  };

  const handleWordMouseUp = () => {
    if (!isSelecting) return;

    setIsSelecting(false);

    if (
      selectionStart != null &&
      selectionEnd != null &&
      selectionStart !== selectionEnd
    ) {
      setShowSelectionMenu(true);
    }
  };

  useEffect(() => {
    clearSelection();
  }, [segment?.id]);

  useEffect(() => {
    if (!enableWordActions || !showWords) clearSelection();
  }, [enableWordActions, showWords]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSelecting) {
        setIsSelecting(false);

        if (
          selectionStart != null &&
          selectionEnd != null &&
          selectionStart !== selectionEnd
        ) {
          setShowSelectionMenu(true);
        }
      }
    };

    const handleDocumentMouseDown = (e) => {
      if (!selectionAreaRef.current) return;

      if (!selectionAreaRef.current.contains(e.target)) {
        clearSelection();
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isSelecting, selectionStart, selectionEnd]);

  const mappedAssessmentWords = useMemo(() => {
    const assessmentWords = assessment?.pronunciation?.NBest?.[0]?.Words || [];
    return buildSegmentAssessmentMap(segment?.words || [], assessmentWords);
  }, [segment?.words, assessment]);

  const isFirstSegment = Number(segment?.id ?? 0) <= 0;
  const isLastSegment =
    Number(segment?.id ?? 0) >= Math.max(0, segments.length - 1);

  const sentenceCounter = `Sentence ${Number(segment?.id ?? 0) + 1}/${
    segments.length
  }`;

  const handleStageKeyDown = (event) => {
    const targetTag = event.target?.tagName?.toLowerCase();

    if (
      targetTag === "button" ||
      targetTag === "input" ||
      targetTag === "textarea" ||
      targetTag === "select"
    ) {
      return;
    }

    if (event.key === "ArrowLeft" && !isFirstSegment) {
      event.preventDefault();
      handlePreviousSegment?.();
    }

    if (event.key === "ArrowRight" && !isLastSegment) {
      event.preventDefault();
      handleNextSegment?.();
    }
  };

  const renderSentenceStatus = () => {
    if (!showCenterCue) {
      return (
        <div className="inline-flex min-h-[28px] items-center gap-2 rounded-full border border-blue-200/60 bg-white/75 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur-sm">
          <span
            className="h-1.5 w-1.5 rounded-full bg-blue-500/75"
            aria-hidden="true"
          />
          <span className="max-w-[65vw] truncate">
            {sentenceLabel || sentenceCounter}
          </span>
        </div>
      );
    }

    return (
      <div className="inline-flex min-h-[28px] items-center gap-2 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm backdrop-blur-sm">
        <span
          className={[
            "transition-all duration-200",
            practiceMode === "shadowing" &&
            recordingStatus === "recording"
              ? "scale-105 text-emerald-600"
              : practiceMode === "echoing"
              ? "text-blue-700"
              : "text-amber-700",
          ].join(" ")}
        >
          {practiceMode === "shadowing" &&
          recordingStatus === "recording"
            ? "Go!"
            : "Get ready…"}
        </span>

        {practiceMode === "shadowing" &&
          isCueActive &&
          recordingStatus !== "recording" && (
            <div className="flex items-center gap-1" aria-hidden="true">
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  cuePulseCount >= 1
                    ? "bg-amber-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
              <span className="h-0.5 w-3 bg-slate-300" />
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  cuePulseCount >= 2
                    ? "bg-orange-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
              <span className="h-0.5 w-3 bg-slate-300" />
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  cuePulseCount >= 3
                    ? "animate-pulse bg-emerald-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
            </div>
          )}

        {practiceMode === "echoing" &&
          echoingPhase === "paused" && (
            <div className="flex items-center gap-1" aria-hidden="true">
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  echoingPulseCount >= 1
                    ? "bg-blue-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
              <span className="h-0.5 w-3 bg-slate-300" />
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  echoingPulseCount >= 2
                    ? "bg-indigo-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
              <span className="h-0.5 w-3 bg-slate-300" />
              <span
                className={[
                  "h-2 w-2 rounded-full transition-colors",
                  echoingPulseCount >= 3
                    ? "animate-pulse bg-emerald-500"
                    : "bg-slate-300",
                ].join(" ")}
              />
            </div>
          )}
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/50 bg-white/80 shadow-lg shadow-blue-500/5 backdrop-blur-sm">
      <div className="relative flex min-h-[52px] items-center justify-center px-3 py-2 sm:px-4">
        <div className="flex min-w-0 items-center justify-center gap-2">
          {showWords ? renderHelpPill() : null}
          {showWords ? renderCommentsPill?.() : null}
        </div>

        {showWords && isMetadataNoHelp && (
          <button
            type="button"
            onClick={() => setShowExerciseHelp(true)}
            className="absolute right-3 inline-flex h-9 items-center gap-1.5 rounded-xl border border-indigo-200/70 bg-white/75 px-3 text-xs font-bold text-indigo-700 shadow-sm transition-all duration-200 hover:-translate-y-px hover:bg-white hover:shadow-md sm:right-4 sm:h-10 sm:rounded-full sm:px-4 sm:text-sm"
            title="About this course"
          >
            <span aria-hidden="true">✨</span>
            <span className="hidden sm:inline">About this course</span>
            <span className="sm:hidden">About</span>
            <span className="opacity-60" aria-hidden="true">›</span>
          </button>
        )}
      </div>

      <div
        ref={selectionAreaRef}
        tabIndex={0}
        onKeyDown={handleStageKeyDown}
        className="relative overflow-hidden rounded-2xl border border-blue-200/40 bg-gradient-to-r from-blue-50/60 via-white/75 to-indigo-50/60 outline-none transition-shadow duration-200 focus-visible:ring-2 focus-visible:ring-blue-400/60 focus-visible:ring-offset-2"
        aria-label={`${sentenceCounter}. Use the left and right arrow keys to change sentence.`}
      >
        <button
          type="button"
          onClick={handlePreviousSegment}
          disabled={isFirstSegment}
          aria-label="Previous sentence"
          title={isFirstSegment ? "This is the first sentence" : "Previous sentence"}
          className={[
            "group absolute inset-y-0 left-0 z-30 flex w-12 items-center justify-start border-r border-blue-200/25 px-3 transition-all duration-200 sm:w-32 sm:justify-center sm:px-4",
            isFirstSegment
              ? "cursor-not-allowed bg-white/25 text-slate-300"
              : "bg-gradient-to-r from-white/95 via-white/65 to-transparent text-slate-600 hover:from-blue-100/95 hover:via-blue-50/70 hover:text-blue-700",
          ].join(" ")}
        >
          <span
            className="text-xl leading-none transition-transform duration-200 group-hover:-translate-x-1"
            aria-hidden="true"
          >
            ‹
          </span>
          <span className="ml-2 hidden text-sm font-semibold sm:inline">
            Previous
          </span>
        </button>

        <button
          type="button"
          onClick={handleNextSegment}
          disabled={isLastSegment}
          aria-label="Next sentence"
          title={isLastSegment ? "This is the last sentence" : "Next sentence"}
          className={[
            "group absolute inset-y-0 right-0 z-30 flex w-12 items-center justify-end border-l border-blue-200/25 px-3 transition-all duration-200 sm:w-32 sm:justify-center sm:px-4",
            isLastSegment
              ? "cursor-not-allowed bg-white/25 text-slate-300"
              : "bg-gradient-to-l from-white/95 via-white/65 to-transparent text-slate-600 hover:from-blue-100/95 hover:via-blue-50/70 hover:text-blue-700",
          ].join(" ")}
        >
          <span className="mr-2 hidden text-sm font-semibold sm:inline">
            Next
          </span>
          <span
            className="text-xl leading-none transition-transform duration-200 group-hover:translate-x-1"
            aria-hidden="true"
          >
            ›
          </span>
        </button>

        <div className="relative z-10 px-14 py-3 sm:px-36 sm:py-4">
          <div className="mb-3 flex min-h-[28px] items-center justify-center">
            {renderSentenceStatus()}
          </div>

        {enableWordActions &&
          showWords &&
          showSelectionMenu &&
          normalizedSelection &&
          practiceMode !== "shadowing" && (
            <div className="absolute left-1/2 top-3 -translate-x-1/2 z-50 pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-2 px-2 py-2 rounded-2xl bg-slate-900/95 text-white shadow-2xl border border-slate-700">
                <button
                  type="button"
                  onClick={playSelectedRange}
                  className="px-3 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm font-medium transition whitespace-nowrap"
                >
                  ▶ Play selected
                </button>

                <button
                  type="button"
                  onClick={clearSelection}
                  className="px-3 py-1.5 rounded-xl bg-white text-gray-700 hover:bg-slate-100 text-sm font-medium transition whitespace-nowrap border border-slate-300 shadow-sm"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {showWordLane ? (
            <div>
        {showWords ? (
        <div className="flex flex-wrap gap-2 justify-center items-center">
          {segment?.words?.map((item, i) => {
            const matchedAssessment = mappedAssessmentWords[i];
            const pronunciation = matchedAssessment?.PronunciationAssessment;
            const phonemes = matchedAssessment?.Phonemes || [];
            const score = pronunciation?.AccuracyScore;
            const errorType = pronunciation?.ErrorType;

            const isActive = currentWord === i;

            const isShadowCueWord =
              practiceMode === "shadowing" &&
              isCueActive &&
              cuePulseCount >= 1 &&
              i === 0;

            const isInSelection =
              normalizedSelection &&
              i >= normalizedSelection.start &&
              i <= normalizedSelection.end;

            const isMultiSelection =
              normalizedSelection &&
              normalizedSelection.start !== normalizedSelection.end;

            const onWordClick = !enableWordActions
              ? undefined
              : practiceMode === "shadowing"
              ? () => handleWordPlay(i, item)
              : (e) => {
                  if (isMultiSelection) return;
                  openWordMenu(e, item.text, i);
                };

            const assessmentClass = getWordAssessmentClasses(score, errorType);

            const idleClass = [assessmentClass, "hover:shadow-md"].join(" ");
            const activeClass = getActiveRingClass(practiceMode);

            const selectionClass =
              "bg-gradient-to-r from-green-300 to-green-300 text-white font-semibold border border-green-500 shadow-lg shadow-green-300/30 scale-105";

            const ringClass =
              practiceMode === "manual"
                ? "ring-1 ring-emerald-200/60"
                : practiceMode === "echoing"
                ? "ring-1 ring-indigo-200/60"
                : "ring-1 ring-orange-200/60";

            return (
              <span
                key={i}
                onClick={onWordClick}
                onMouseDown={
                  enableWordActions ? (e) => handleWordMouseDown(i, e) : undefined
                }
                onMouseEnter={
                  enableWordActions ? () => handleWordMouseEnter(i) : undefined
                }
                onMouseUp={enableWordActions ? handleWordMouseUp : undefined}
                className={[
                  "text-lg sm:text-xl font-medium px-3.5 py-2.5 rounded-xl transition-all duration-300 cursor-pointer relative overflow-hidden select-none flex flex-col items-center gap-0.5 border min-w-[68px]",
                  !isInSelection ? ringClass : "",
                  isInSelection
                    ? selectionClass
                    : isActive
                    ? `${idleClass} ${activeClass}`
                    : idleClass,
                ].join(" ")}
                title={
                  enableWordActions && matchedAssessment
                    ? `${matchedAssessment?.Word || item.text}${
                        typeof score === "number" ? ` • Score: ${score}` : ""
                      }${errorType ? ` • ${errorType}` : ""}`
                    : practiceMode === "shadowing"
                    ? "Tap to play from here"
                    : "Drag to select words"
                }
              >
                <div className="flex items-center justify-center gap-1 h-2">
                  {phonemes.length > 0 ? (
                    phonemes.map((ph, idx) => (
                      <span
                        key={idx}
                        className={`w-2 h-2 rounded-full ${getPhonemeDotColor(
                          ph?.PronunciationAssessment?.AccuracyScore
                        )} ring-1 ring-white shadow-sm`}
                        title={`${ph?.Phoneme || "phoneme"}: ${
                          ph?.PronunciationAssessment?.AccuracyScore ?? "N/A"
                        }`}
                      />
                    ))
                  ) : matchedAssessment ? (
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${getWordScoreColor(
                        score,
                        errorType
                      )} ring-1 ring-white shadow-sm`}
                    />
                  ) : null}
                </div>

                <span className="text-gray-800 text-center leading-tight break-words">
                  {item.text}
                </span>

                {isShadowCueWord && (
                  <div className="absolute inset-0 bg-white/20 animate-ping rounded-xl" />
                )}
              </span>
            );
          })}
        </div>
        ) : (
          <div
            className="flex min-h-[72px] flex-wrap items-center justify-center gap-2"
            aria-label="Sentence words are hidden"
          >
            {segment?.words?.map((item, i) => {
              const isActive = isPlayingSegment && currentWord === i;
              const estimatedWidth = Math.max(68, Math.min(180,
                String(item?.text || "").length * 11 + 28
              ));

              return (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{ width: `${estimatedWidth}px` }}
                  className={[
                    "h-[50px] rounded-xl border transition-all duration-300",
                    "bg-white/70 border-indigo-200/70 ring-1 ring-indigo-200/60",
                    isActive
                      ? getActiveRingClass("echoing")
                      : "shadow-sm",
                  ].join(" ")}
                />
              );
            })}
          </div>
        )}
            </div>
          ) : (
            <div className="min-h-[28px]" aria-hidden="true" />
          )}
        </div>
      </div>
    </div>
  );
};

export default SentencePracticeBlock;